<template>
  <Tippy :content="instruction">
    <FavouriteHeart
      class="bg-transparent p-2"
      :favourited="(favourited || forceOn) && !forceOff"
      :heartClass="heartClass"
      :heartSize="heartSize"
      :showShadow="true"
      :showStroke="false"
      @addFavourite="runAction"
      @removeFavourite="runAction"
    />
  </Tippy>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import FavouriteHeart from '@/components/favourites/FavouriteHeart'
import Tippy from '@/components/Tippy'
import { useLogos } from '@/lib/hooks'
import { EventBus, Events } from '@/lib/events'
import { processAndFavoriteClientSideLogo } from '../../lib/helpers/logos'
import { useSessionStorage } from '@/lib/helpers/storage'

export default {
  components: {
    FavouriteHeart,
    Tippy,
  },

  props: {
    prompt: { type: String, default: '' },
    type: { type: String, default: 'logo' },
    logo: { type: Object, default: null },
    heartClass: { type: String, default: '' },
    heartSize: { type: String, default: 'w-6 h-6' },
  },

  data: () => ({
    forceOn: false,
    forceOff: false,
    user: null,
    favoritedLogo: null,
  }),

  computed: {
    favourited() {
      let logo_id = this.logo.id
      if (this.favoritedLogo) {
        logo_id = this.favoritedLogo.id
      }
      return this.favourites.logos.some(favLogo => favLogo.id === logo_id)
    },

    api() {
      return `/api/v2/logos/favourites/user_${this.user}/${this.logo.id}`
    },

    errorText() {
      const actioning = this.favourited ? 'unsaving' : 'saving'

      return `There was an issue ${actioning} that ${this.type}`
    },

    errorTextFull() {
      return `${this.errorText} - please refresh and try again`
    },

    instruction() {
      const item = this.prompt || this.type

      return `${this.favourited ? 'Unsave' : 'Save'} this ${item}`
    },

    ...mapState(['favourites', 'enterprise', 'flags']),
  },

  mounted() {
    this.setUser()
    EventBus.$on(Events.EditorRefreshLogo, this.clearLogo)
  },

  beforeDestroy() {
    EventBus.$off(Events.EditorRefreshLogo, this.clearLogo)
  },

  methods: {
    clearLogo() {
      this.favoritedLogo = null
    },
    async setUser() {
      try {
        const auth = await this.$getAuthInstance()
        const { attributes } = await auth.currentAuthenticatedUser()
        // Using sub here, as Cognito sets username to some weird,
        // useless strings when using social logins, and the username is
        // changeable which can cause more weirdness
        this.user = attributes.sub
      } catch (error) {
        // We probably don't want to log out the error or there will be
        // many spams
        this.user = null
      }
    },

    addLogoToSessionStorage() {
      useSessionStorage().setItem('logo_to_favorite', JSON.stringify(this.logo))
    },

    removeLogoFromSessionStorage() {
      useSessionStorage().removeItem('logo_to_favorite')
    },

    async runAction() {
      // Wait for the user again in case they've logged in since we mounted()
      await this.setUser()
      if (this.user) {
        if (this.favourited) {
          this.remove()
        } else {
          this.add()
        }
      } else {
        try {
          // Sling the logo ID into the query in case we auth with socials
          this.setQuery(this.logo.id)
          this.addLogoToSessionStorage()

          await new Promise((resolve, reject) => {
            EventBus.$emit(Events.UserOpenAuthModal, {
              scope: 'Editor Login/Register',
              modalType: 'register',
              canClose: true,
              resolve,
              reject,
            })
          })

          await this.setUser()
          if (this.favourited) {
            this.remove()
          } else {
            this.add()
          }
        } catch (e) {
          // Do nothing
        }
        // Remove the query
        this.setQuery()
      }
    },

    /**
     * Set the add_favourite query param
     */
    setQuery(value = undefined) {
      this.$router.replace({
        path: this.$route.path,
        query: {
          ...this.$route.query,
          app_return_path: this.$route.path,
          add_favourite: value,
        },
      })
    },

    async add() {
      const { favoriteClientSideLogo } = useLogos(this.$api)

      // Force the heart to look favourited instantly
      this.forceOn = true

      try {
        let logo
        if (this.flags.client_side_logos) {
          logo = await processAndFavoriteClientSideLogo(
            this.logo,
            this.enterprise.id,
            favoriteClientSideLogo
          )

          this.favoritedLogo = logo
        } else {
          const data = await this.$api.post(this.api, {})
          logo = data.data
        }

        EventBus.$emit(Events.AnalyticsSelectLogo, { logo, list: 'favourites' })

        this.addLogo(logo)
      } catch (error) {
        this.$toast.error(this.errorTextFull)
        this.$sentry.captureException(error)
        console.log('error while favoriting logo', error)
      }

      // Remove the temporary force on
      this.forceOn = false

      // if we've made it this far the logo saved in the session
      // should be removed
      this.removeLogoFromSessionStorage()
    },

    async remove() {
      const { unfavoriteClientSideLogo } = useLogos(this.$api)

      // Force the heart to look not favourited instantly
      this.forceOff = true

      try {
        const logo = await unfavoriteClientSideLogo(
          this.favoritedLogo || this.logo
        )

        this.removeLogo(logo)
      } catch (error) {
        this.$toast.error(this.errorTextFull)
        this.$sentry.captureException(error)
        console.log('error while unfavoriting logo', error)
      }

      // Remove the temporary force off
      this.forceOff = false
    },

    ...mapActions({
      fetchFavourites: 'favourites/fetchFavouriteLogos',
      addLogo: 'favourites/addLogo',
      removeLogo: 'favourites/removeLogo',
    }),
  },
}
</script>
